import type { BeforeSendFn, CaptureResult } from "posthog-js";

const GENERIC_SCRIPT_ERROR_MESSAGE = "Script error.";
const SERVICE_NAME = "worship-devo";

const EXTENSION_RUNTIME_SEND_MESSAGE_TAB_NOT_FOUND = /runtime\.sendMessage\(\).*Tab not found/i;

interface PostHogExceptionContext {
  deploymentEnvironment?: string;
}

export function isGenericScriptErrorMessage(message: unknown): boolean {
  return message === GENERIC_SCRIPT_ERROR_MESSAGE;
}

export function isExtensionRuntimeSendMessageError(message: unknown): boolean {
  return typeof message === "string" && EXTENSION_RUNTIME_SEND_MESSAGE_TAB_NOT_FOUND.test(message);
}

export function isIgnorableExceptionMessage(message: unknown): boolean {
  return isGenericScriptErrorMessage(message) || isExtensionRuntimeSendMessageError(message);
}

function getExceptionValues(properties: Record<string, unknown>): Array<string> {
  const directValues = properties.$exception_values;
  if (Array.isArray(directValues)) {
    return directValues.filter((value): value is string => typeof value === "string");
  }

  const exceptionList = properties.$exception_list;
  if (!Array.isArray(exceptionList)) {
    return [];
  }

  return exceptionList.flatMap((entry) => {
    if (!entry || typeof entry !== "object" || !("value" in entry)) {
      return [];
    }

    const value = entry.value;
    return typeof value === "string" ? [value] : [];
  });
}

export function shouldSendPostHogExceptionEvent(properties: Record<string, unknown>): boolean {
  const exceptionValues = getExceptionValues(properties);

  if (exceptionValues.length === 0) {
    return true;
  }

  return !exceptionValues.every(isIgnorableExceptionMessage);
}

export function createPostHogBeforeSend(context: PostHogExceptionContext = {}): BeforeSendFn {
  return (event: CaptureResult | null): CaptureResult | null => {
    if (!event || event.event !== "$exception") {
      return event;
    }

    if (!shouldSendPostHogExceptionEvent(event.properties)) {
      return null;
    }

    event.properties.service_name ??= SERVICE_NAME;
    if (context.deploymentEnvironment) {
      event.properties.deployment_environment ??= context.deploymentEnvironment;
    }

    return event;
  };
}
